import React from 'react';
import { Box, GridProps } from '@mui/material';
import {
  AfterRenderSurveyEvent,
  CompletingEvent,
  Model,
  surveyLocalization,
} from 'survey-core';
import { Survey } from 'survey-react-ui';
import { parseJSONCleaned } from 'utilities/index';
import { SparkButton, SparkScrollableGridContainer } from 'sharedComponents/UI';
import { PROPERTY_TYPES } from 'constants/surveyJS/surveyOwnContants';
import type {
  CommonRecommendationSchema,
  RatingRecommendationSchema,
  RecommendedValues,
  SparkRunInputParameterRecommendation,
  TagboxRecommendationSchema,
} from 'types/Spark/SparkRunInputParameterRecommendations';
import './surveyJS.css';
import './helpers/survey-popover.css';
import ReactDOM from 'react-dom/client';
import RecommendationsPopover from 'components/SparkRuns/SparkRunPageForm/RecommendationsPopover';
import { useBlobStorage } from 'hooks/useBlobStorage';
import { MAX_FILE_SIZE } from 'constants/index';
import { QueryClientProvider } from 'react-query';
import { client } from 'index';
import SparkThemeProvider from 'components/Providers/ThemeProvider';
import { Star02 } from 'components/SVG';
import { useCurrentRoute } from 'hooks/common';

const engLocale = surveyLocalization.locales['en'];
// Override individual translations
engLocale.emptySurvey = 'There is no output to display';

export type SparkUp = {
  sparkInputParameterID: string;
  parameterID: string;
};

export const restoreDataFromLocalStorage = (surveyID: string) => {
  return window.localStorage.getItem(surveyID);
};

type SparkSurveyFormProps = {
  id: string;
  disabled?: boolean;
  formConfigJSON: string;
  showCompleteButton?: boolean;
  showProgressBar?:
    | 'off'
    | 'aboveHeader'
    | 'belowHeader'
    | 'bottom'
    | 'topBottom'
    | 'auto'
    | 'top'
    | 'both';
  height?: React.CSSProperties['height'];
  recommendations?: SparkRunInputParameterRecommendation[] | null;
  previewMode?: boolean;
  floatingAction?: boolean;
  formContainerStyles?: GridProps['sx'];
  initialFormValues?: string;
  onComplete?: () => Promise<void>;
  shouldProgressBarRender?: boolean;
  onCompleting?: (completingEvent: CompletingEvent) => void;
  onValueChanged?: (formValues: Record<string, string | number>) => void;
  onAfterRenderSurvey?: (
    sender: Model,
    options: AfterRenderSurveyEvent,
    currentFormValues: Record<string, string> | null
  ) => void;
  isReadOnly?: boolean;
  actionContainerStyles?: GridProps['sx'];
};
function SparkSurveyForm({
  id,
  disabled = false,
  formConfigJSON,
  showCompleteButton = true,
  showProgressBar = 'aboveHeader',
  formContainerStyles = {},
  previewMode = false,
  height,
  recommendations,
  onComplete,
  onCompleting,
  onValueChanged,
  onAfterRenderSurvey,
  floatingAction,
  initialFormValues,
  shouldProgressBarRender = true,
  isReadOnly = false,
  actionContainerStyles = {},
}: SparkSurveyFormProps) {
  const [isLastStep, setIsLastStep] = React.useState(false);
  const [isFirstStep, setIsFirstStep] = React.useState(true);
  const formCleanConfig = parseJSONCleaned(formConfigJSON);
  const survey = new Model(JSON.stringify(formCleanConfig));

  const [pageNo, setPageNo] = React.useState(survey.currentPageNo);
  const { handleFileUpload } = useBlobStorage('public-assets');
  const { isSparkRun } = useCurrentRoute();

  survey.showCompleteButton = showCompleteButton;
  survey.showProgressBar = showProgressBar;
  survey.showCompletedPage = false;
  survey.surveyId = id;
  survey.showNavigationButtons = false;

  survey.onAfterRenderSurvey.add(function (sender, options) {
    // set values to questions if available in local storage
    const questions = survey.getAllQuestions(true, false, false);
    const initialValues = parseJSONCleaned<Record<string, string>>(
      initialFormValues || ''
    );
    const currentFormValues =
      initialValues ||
      parseJSONCleaned<Record<string, string>>(
        restoreDataFromLocalStorage(survey.surveyId) || ''
      );

    questions.forEach((question) => {
      const questionType = question.getType();
      if (questionType === 'file') {
        question.storeDataAsText = false;
        question.allowMultiple = true;
        question.maxSize = MAX_FILE_SIZE;
        question.waitForUpload = true;
        question.acceptedTypes = '.jpg,.jpeg,.png,.doc,.docx,.pdf,.txt,.svg';
      }

      question.value = currentFormValues?.[question.name];
      question.readOnly = disabled || isReadOnly;
    });
    setIsFirstStep(sender.isFirstPage);
    setIsLastStep(sender.isLastPage || sender.pageCount === 1);
    onAfterRenderSurvey &&
      onAfterRenderSurvey(sender, options, currentFormValues);
  });

  if (recommendations) {
    survey.onAfterRenderQuestion.add((sender, options) => {
      const question = options.question;

      const questionRecommendation = recommendations.find(
        (recommendation) =>
          recommendation.attributes.parameterName === question.name
      );

      const parsedRecommendations =
        parseJSONCleaned<RecommendedValues>(
          questionRecommendation?.attributes.recommendedValues || ''
        ) || {};

      if (!parsedRecommendations) {
        return;
      }
      const questionType = question.getType();
      if (questionType === PROPERTY_TYPES.BOOLEAN) {
        const { recommendedValue } = parsedRecommendations;
        options.question.value = !!recommendedValue;
        return;
      }

      if (!parsedRecommendations.recommendedValues) {
        return;
      }

      if (questionType === PROPERTY_TYPES.TAG_BOX) {
        const recommendedValues =
          parsedRecommendations.recommendedValues as TagboxRecommendationSchema;
        options.question.value = recommendedValues.map((value) => value.tag);
        return;
      }
      if (questionType === PROPERTY_TYPES.RATTING) {
        const recommendedValues =
          parsedRecommendations.recommendedValues as RatingRecommendationSchema;
        options.question.value = recommendedValues.map((value) => value.value);
        return;
      }

      const recommendedValues =
        parsedRecommendations.recommendedValues as CommonRecommendationSchema;

      if (
        questionType === PROPERTY_TYPES.TEXT ||
        questionType === PROPERTY_TYPES.COMMENT
      ) {
        question.value = recommendedValues[0].value;
        const inputField = options.htmlElement.querySelector('input');
        const textArea = options.htmlElement.querySelector('textarea');
        const element = inputField || textArea;

        let wrapper = document.createElement('div') as HTMLElement;
        wrapper.id = 'wow';
        if (element) {
          const inputFieldWidth = element.getBoundingClientRect().width;

          const wrapperId = `my-component-wrapper-${question.name}`;

          wrapper.id = wrapperId;
          wrapper.style.top = '5px';
          wrapper.style.left = '0';
          wrapper.style.display = 'none';
          wrapper.style.position = 'relative';
          wrapper.style.width = `${inputFieldWidth + 47}px`;
          wrapper.style.maxWidth = '100%';
        }

        sender.onFocusInQuestion.add((_, op) => {
          if (op.question.name === question.name) {
            wrapper.style.display = 'block';
          }
        });

        document.addEventListener('click', function () {
          const focusedElement = document.activeElement;

          if (!focusedElement || !focusedElement.closest('.sd-question')) {
            wrapper.style.display = 'none';
          }
        });

        options.htmlElement.appendChild(wrapper);

        document.addEventListener('click', (event) => {
          if (!options.htmlElement.contains(event.target as any)) {
            wrapper.style.display = 'none';
          }
        });
        const handleClick = (
          event: React.MouseEvent<HTMLLIElement, MouseEvent>,
          value: string
        ) => {
          event.preventDefault();
          event.stopPropagation();
          question.value = value;
          wrapper.style.display = 'none';
        };
        const root = ReactDOM.createRoot(wrapper);
        root.render(
          <SparkThemeProvider>
            <QueryClientProvider client={client}>
              <RecommendationsPopover
                onMenuItemClick={handleClick}
                recommendedValues={recommendedValues}
                sparkRunInputRecommendationID={questionRecommendation?.id}
              />
            </QueryClientProvider>
          </SparkThemeProvider>
        );
        return;
      }
      options.question.value = recommendedValues[0].value;
    });
  }

  survey.onComplete.add(() => {
    if (disabled || previewMode) {
      return;
    }

    onComplete && onComplete();
  });

  onCompleting &&
    survey.onCompleting.add((_, options) => {
      onCompleting(options);
    });

  survey.onValueChanged.add((survey) => {
    window.localStorage.setItem(survey.surveyId, JSON.stringify(survey.data));
    onValueChanged && onValueChanged(survey.data);
  });

  const prevPage = () => {
    survey.prevPage();
  };
  const nextPage = () => {
    survey.nextPage();
  };
  const endSurvey = () => {
    if (previewMode) {
      return;
    }
    survey.completeLastPage();
  };

  survey.onCurrentPageChanged.add((model, options) => {
    setPageNo(options.newCurrentPage.visibleIndex);
    setIsFirstStep(model.isFirstPage);
    setIsLastStep(model.isLastPage);
  });

  survey.onUploadFiles.add(async (sender, options) => {
    const files = options.files;

    const uploads = await handleFileUpload(files, [], false);

    options.callback(
      options.files.map((file) => {
        const upload = uploads?.find(
          (upload) => upload.file.name === file.name
        );
        return {
          file: file,
          content: upload?.url,
        };
      })
    );
  });

  // survey.onErrorCustomText.add((sender, options) => {
  // });

  survey.onDownloadFile.add((_, options) => {
    fetch(options.content)
      .then((response) => response.blob())
      .then((blob) => {
        const file = new File([blob], options.fileValue.name, {
          type: options.fileValue.type,
        });
        const reader = new FileReader();
        reader.onload = (e) => {
          options.callback('success', e.target?.result);
        };
        reader.readAsDataURL(file);
      })
      .catch((error) => {
        console.error('Error: ', error);
        options.callback('error');
      });
  });
  const getActions = () => {
    return (
      <Box
        display='flex'
        justifyContent='space-between'
        className='gradient-blur'
      >
        <SparkButton
          onClick={prevPage}
          variant='outlined'
          disabled={disabled || isFirstStep}
          sx={{
            color: 'black',
            visibility: isFirstStep ? 'hidden' : 'unset',
            paddingY: 1.5,
            paddingX: 3,
            bgcolor: 'white',
          }}
        >
          Previous
        </SparkButton>
        <SparkButton
          onClick={isLastStep ? endSurvey : nextPage}
          variant='contained'
          disabled={disabled || (isLastStep && !isSparkRun)}
          sx={{ paddingY: 1.5, paddingX: 3 }}
          startIcon={
            isLastStep && !previewMode ? <Star02 color='white' /> : null
          }
        >
          {isLastStep && !previewMode ? 'Run Spark' : 'Next'}
        </SparkButton>
      </Box>
    );
  };
  if (!showCompleteButton) {
    return <Survey model={survey} currentPageNo={pageNo} />;
  }
  return (
    <SparkScrollableGridContainer
      actions={getActions()}
      height={height}
      scrollableContainerStyle={{ padding: 0 }}
      floatingAction={floatingAction}
      sx={{
        ...formContainerStyles,
        '& .sd-progress-buttons__container': {
          display: shouldProgressBarRender ? 'block' : 'none',
        },
        '& .sd-progress-buttons__container-center': {
          paddingTop: shouldProgressBarRender ? '' : '0',
          paddingBottom: shouldProgressBarRender ? '' : '0',
        },
        '& .sd-progress-buttons__page-title': {
          margin: shouldProgressBarRender ? '' : '0',
        },
      }}
      actionContainerStyles={actionContainerStyles}
    >
      <Survey model={survey} currentPageNo={pageNo} />
    </SparkScrollableGridContainer>
  );
}

export default SparkSurveyForm;
